import React, { useState, useEffect } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { Button, Modal, Navbar, NavbarToggle, DarkThemeToggle } from "flowbite-react";
import { HiChartPie, HiCalendar, HiOutlineExclamationCircle, HiInformationCircle, HiChartBar, HiCube } from "react-icons/hi";
import { IoPower, IoMoon, IoSunny, IoCloudDownloadSharp, IoDocumentsSharp } from "react-icons/io5";

const packageJson = require("../../package.json");
const version = packageJson.version;

const Menu = ({ onNavigate, currentPage, yearlySummary }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isAutoTradesOpen, setIsAutoTradesOpen] = useState(false);

  const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);
  const toggleAutoTrades = () => setIsAutoTradesOpen(!isAutoTradesOpen);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsDrawerOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSignOut = () => {
    signOut(auth)
      .then(() => {
        onNavigate("landing");
        setOpenModal(false);
      })
      .catch((error) => {
        console.error("Sign out error", error);
        setOpenModal(false);
      });
  };

  const MenuContent = () => (
    <div className="flex flex-col h-full justify-between p-4">
      <div>
        <div className="mb-10">
          <h1 className="text-4xl font-bold text-cyan-400">TradeWise</h1>
          <p className={`text-lg ${yearlySummary.totalPL >= 0 ? "text-green-500" : "text-red-500"}`}>
            Total P/L: ${yearlySummary.totalPL.toFixed(2)}
          </p>
        </div>
        <div className="space-y-4">
          <Button
            className="w-full text-left justify-start"
            color={currentPage === "dashboard" ? "cyan" : "gray"}
            onClick={() => {
              onNavigate("dashboard");
              toggleDrawer();
            }}
          >
            <HiChartPie className="mr-2" />
            Dashboard
          </Button>
          <div>
            <Button
              className="w-full text-left justify-start items-center"
              color={currentPage === "autohistory" || currentPage === "autosettings" ? "cyan" : "gray"}
              onClick={toggleAutoTrades}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center mr-2">
                  <HiCube className="mr-2" />
                  Auto Trades
                </div>
                <svg
                  className={`w-3 h-3 ${isAutoTradesOpen ? "transform rotate-180" : ""}`}
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 10 6"
                >
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 4 4 4-4" />
                </svg>
              </div>
            </Button>
            {isAutoTradesOpen && (
              <div className="pl-4 mt-2 space-y-2">
                <Button
                  className="w-full text-left justify-start"
                  color={currentPage === "autosettings" ? "cyan" : "gray"}
                  onClick={() => {
                    onNavigate("autosettings");
                    toggleDrawer();
                  }}
                >
                  <HiChartBar className="mr-2" />
                  Auto Settings
                </Button>
                <Button
                  className="w-full text-left justify-start"
                  color={currentPage === "autohistory" ? "cyan" : "gray"}
                  onClick={() => {
                    onNavigate("autohistory");
                    toggleDrawer();
                  }}
                >
                  <HiCube className="mr-2" />
                  Auto History
                </Button>
              </div>
            )}
          </div>
          <Button
            className="w-full text-left justify-start"
            color={currentPage === "faq" ? "cyan" : "gray"}
            onClick={() => {
              onNavigate("faq");
              toggleDrawer();
            }}
          >
            <HiCalendar className="mr-2" />
            FAQ
          </Button>
        </div>
      </div>

      {/* CTA Button */}
      <div className="my-4 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
        <div className="flex items-center mb-3">
          <HiInformationCircle className="mr-2 h-5 w-5 text-blue-500" />
          <span className="text-sm font-medium text-blue-500">Alpha {version}</span>
        </div>
        <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
          TradeWise is currently in its Alpha stage. We’re continuously updating it with new features and improvements. Please
          note that T212 does not provide CFD data via API, so we must use PDF and email documents instead. We plan to add other
          CFD brokers soon.
        </p>
        {/* <a
          className="inline-flex items-center text-xs font-medium text-blue-500 hover:underline"
          href="https://twitter.com/iamdihan"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaXTwitter className="mr-2" />
          Feedback? X me @iamdihan
        </a> */}
      </div>

      <div className="space-y-4">
        <Button
          className={`w-full ${
            currentPage === "datauploader" ? "dark:bg-cyan-500 bg-cyan-500" : "dark:bg-green-500 bg-green-500"
          } text-white py-2 px-4 rounded-lg flex items-center justify-center`}
          onClick={() => {
            onNavigate("datauploader");
            toggleDrawer();
          }}
        >
          <IoDocumentsSharp className="mr-2" size={25} />
          Import Trades Files
        </Button>

        <Button
          className={`w-full ${
            currentPage === "importcapital" ? "dark:bg-cyan-500 bg-cyan-500" : "dark:bg-green-500 bg-green-500"
          } text-white py-2 px-4 rounded-lg flex items-center justify-center`}
          onClick={() => {
            onNavigate("importcapital");
            toggleDrawer();
          }}
        >
          <IoCloudDownloadSharp className="mr-2" size={25} />
          Import Trades API
        </Button>
        <div className="flex items-center justify-between">
          <DarkThemeToggle iconDark={IoMoon} iconLight={IoSunny} />
          <Button
            onClick={() => {
              onNavigate("account");
              toggleDrawer();
            }}
            color="gray"
          >
            Profile
          </Button>
          <Button onClick={() => setOpenModal(true)} color="gray">
            <IoPower className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Navbar className="fixed z-10 lg:hidden w-full justify-between">
        <NavbarToggle onClick={toggleDrawer} />
        <h1 className="text-4xl font-bold text-cyan-400">TradeWise</h1>
      </Navbar>

      {/* Custom Drawer */}
      <div
        className={`fixed min-w-64 inset-y-0 left-0 transform ${
          isDrawerOpen ? "translate-x-0" : "-translate-x-full"
        } w-64 bg-white dark:bg-slate-800 overflow-y-auto ease-in-out transition-all duration-300 z-30 lg:translate-x-0 lg:static lg:inset-0`}
      >
        <MenuContent />
      </div>

      {/* Overlay */}
      {isDrawerOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden" onClick={toggleDrawer}></div>}

      {/* Sign Out Confirmation Modal */}
      <Modal show={openModal} size="md" onClose={() => setOpenModal(false)} popup>
        <Modal.Header />
        <Modal.Body>
          <div className="text-center p-5">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-slate-500 dark:text-slate-400">Are you sure you want to sign out?</h3>
            <div className="flex justify-center gap-4">
              <Button color="failure" onClick={handleSignOut}>
                Yes, sign out
              </Button>
              <Button color="gray" onClick={() => setOpenModal(false)}>
                No, cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Menu;
